import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import { BRANCH_TYPES, COLLEGE_STATUS, type CollegeStatus, getCollegeStatusLabel } from "@/types/enums";
import { normalizeCollegeCode } from "@/utils/collegeCode";
import { formatBranchType } from "@/utils/formatters";
import type { Tables } from "@/integrations/supabase/types";
import { useEffect } from "react";

type College = Tables<"colleges"> & { status?: string };

interface CollegeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  college?: College | null;
  onSubmit: (data: CollegeFormData) => void;
  isSubmitting: boolean;
}

export interface CollegeFormData {
  name: string;
  college_code: string;
  address?: string;
  city?: string;
  courses?: string[];
  branches?: string[];
  salary_expectations?: string;
  placement_coordinator_name?: string;
  placement_coordinator_phone?: string;
  placement_officer_name?: string;
  placement_officer_phone?: string;
  notes?: string;
  drive_start_date?: string;
  drive_end_date?: string;
  college_visit_date?: string;
  ip_range?: string;
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  is_deleted?: boolean;
  status?: CollegeStatus;
  joining_date?: string;
}

export function CollegeDialog({
  open,
  onOpenChange,
  college,
  onSubmit,
  isSubmitting,
}: CollegeDialogProps) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors },
  } = useForm<CollegeFormData>();

  const watchDriveStartDate = watch("drive_start_date");
  const watchDriveEndDate = watch("drive_end_date");
  const watchStartTime = watch("start_time");
  const watchEndTime = watch("end_time");
  const isSameRegistrationDay =
    !watchDriveStartDate || !watchDriveEndDate || watchDriveStartDate === watchDriveEndDate;

  const parseTimeToMinutes = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const getScheduleWindowMinutes = () => {
    if (!watchDriveStartDate || !watchDriveEndDate || !watchStartTime || !watchEndTime) {
      return null;
    }

    const startDateTime = new Date(`${watchDriveStartDate}T${watchStartTime}`);
    const endDateTime = new Date(`${watchDriveEndDate}T${watchEndTime}`);

    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      return null;
    }

    return Math.floor((endDateTime.getTime() - startDateTime.getTime()) / 60000);
  };

  const validateEndDate = (value?: string) => {
    if (watchDriveStartDate && value) {
      if (value < watchDriveStartDate) {
        return "Registration end date cannot be before start date";
      }

      const scheduleWindowMinutes = getScheduleWindowMinutes();
      if (scheduleWindowMinutes != null && scheduleWindowMinutes < 0) {
        return "Registration end date and time must be after the start date and time";
      }
    }
    return true;
  };

  const validateStartDate = (value?: string) => {
    if (watchDriveEndDate && value) {
      if (value > watchDriveEndDate) {
        return "Registration start date cannot be after end date";
      }

      const scheduleWindowMinutes = getScheduleWindowMinutes();
      if (scheduleWindowMinutes != null && scheduleWindowMinutes < 0) {
        return "Registration start date and time must be before the end date and time";
      }
    }
    return true;
  };

  const validateEndTime = (value?: string) => {
    if (watchStartTime && value) {
      if (!watchDriveStartDate || !watchDriveEndDate || watchDriveStartDate === watchDriveEndDate) {
        return (
          parseTimeToMinutes(value) > parseTimeToMinutes(watchStartTime) ||
          "End time must be after start time"
        );
      }
    }
    return true;
  };

  const validateStartTime = (value?: string) => {
    if (watchEndTime && value) {
      if (!watchDriveStartDate || !watchDriveEndDate || watchDriveStartDate === watchDriveEndDate) {
        return (
          parseTimeToMinutes(value) < parseTimeToMinutes(watchEndTime) ||
          "Start time must be before end time"
        );
      }
    }
    return true;
  };

  // Reset form when college data changes or dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: college?.name || "",
        college_code: normalizeCollegeCode(college?.college_code || ""),
        address: college?.address || "",
        city: college?.city || "",
        courses: college?.courses || [],
        branches: college?.branches || [],
        salary_expectations: college?.salary_expectations || "",
        placement_coordinator_name: college?.placement_coordinator_name || "",
        placement_coordinator_phone: college?.placement_coordinator_phone || "",
        placement_officer_name: college?.placement_officer_name || "",
        placement_officer_phone: college?.placement_officer_phone || "",
        notes: college?.notes || "",
        drive_start_date: college?.drive_start_date || "",
        drive_end_date: college?.drive_end_date || "",
        college_visit_date: college?.college_visit_date || "",
        joining_date: college?.joining_date || "",
        ip_range: college?.ip_range || "",
        start_time: college?.start_time || "",
        end_time: college?.end_time || "",
        duration_minutes: college?.duration_minutes || undefined,
        is_deleted: false,
        status: (college?.status as CollegeStatus) || COLLEGE_STATUS.ACTIVE,
      });
    }
  }, [college, open, reset]);

  useEffect(() => {
    if (watchDriveStartDate || watchDriveEndDate) {
      void trigger(["drive_start_date", "drive_end_date", "duration_minutes", "start_time", "end_time"]);
    }
  }, [watchDriveEndDate, watchDriveStartDate, trigger]);

  useEffect(() => {
    if (watchStartTime || watchEndTime) {
      void trigger(["start_time", "end_time", "duration_minutes"]);
    }
  }, [watchEndTime, watchStartTime, trigger]);

  const handleFormSubmit = (data: CollegeFormData) => {
    const cleanedData = {
      ...data,
      college_code: normalizeCollegeCode(data.college_code),
      start_time: data.start_time || null,
      end_time: data.end_time || null,
      duration_minutes:
        data.duration_minutes != null && !isNaN(data.duration_minutes)
          ? data.duration_minutes
          : null,
      ip_range: data.ip_range || null,
      drive_start_date: data.drive_start_date || null,
      drive_end_date: data.drive_end_date || null,
      college_visit_date: data.college_visit_date || null,
      joining_date: data.joining_date || null,
    };
    onSubmit(cleanedData);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  // Static course options
  const courseOptions = [
    { label: "B.Tech", value: "btech" },
    { label: "B.E", value: "be" },
    { label: "B.Sc", value: "bsc" },
    { label: "M.Tech", value: "mtech" },
    { label: "M.E", value: "me" },
    { label: "M.Sc", value: "msc" },
    { label: "PhD", value: "phd" },
    { label: "Diploma", value: "diploma" },
    { label: "BCA", value: "bca" },
    { label: "MCA", value: "mca" },
    { label: "BBA", value: "bba" },
    { label: "MBA", value: "mba" },
  ];

  const branchOptions = Object.values(BRANCH_TYPES).map((branch) => ({
    label: formatBranchType(branch),
    value: branch,
  }));

  // Phone validation regex
  const phonePattern = /^[+]?[1-9][\d]{0,15}$/;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {college ? "Edit College" : "Add New College"}
          </DialogTitle>
          <DialogDescription>
            {college
              ? "Update college information and drive details."
              : "Add a new college partner with drive scheduling details."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">College Name *</Label>
              <Input
                id="name"
                {...register("name", {
                  required: "College name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                })}
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="college_code">College Code *</Label>
              <Input
                id="college_code"
                {...register("college_code", {
                  required: "College code is required",
                  setValueAs: (value) => normalizeCollegeCode(value),
                  minLength: {
                    value: 2,
                    message: "Code must be at least 2 characters",
                  },
                  validate: (value) =>
                    normalizeCollegeCode(value).length > 0 ||
                    "College code is required",
                })}
                placeholder="e.g., TEST-COLLEGE-123"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use a short registration code. It is saved in uppercase URL format.
              </p>
              {errors.college_code && (
                <p className="text-sm text-destructive mt-1">
                  {errors.college_code.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                {...register("city")}
                placeholder="Enter city name"
              />
            </div>

            <div>
              <Label htmlFor="salary_expectations">Salary Expectations</Label>
              <Input
                id="salary_expectations"
                {...register("salary_expectations")}
                placeholder="e.g., 4-8 LPA"
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value || COLLEGE_STATUS.ACTIVE}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(COLLEGE_STATUS).map((s) => (
                        <SelectItem key={s} value={s}>
                          {getCollegeStatusLabel(s as CollegeStatus)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              {...register("address")}
              rows={3}
              placeholder="Enter full address"
            />
          </div>

          {/* Campus Scheduling Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Campus Scheduling</h3>
            <p className="text-sm text-gray-600">
              All times are in Indian Standard Time (IST). Assessment validation will be based on server time to ensure accuracy.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="drive_start_date">Registration Start Date</Label>
                <Input
                  id="drive_start_date"
                  type="date"
                  lang="en-GB"
                  max={watchDriveEndDate || undefined}
                  {...register("drive_start_date", {
                    validate: validateStartDate,
                  })}
                />
                {errors.drive_start_date && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.drive_start_date.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="drive_end_date">Registration End Date</Label>
                <Input
                  id="drive_end_date"
                  type="date"
                  lang="en-GB"
                  min={watchDriveStartDate || undefined}
                  {...register("drive_end_date", {
                    validate: validateEndDate,
                  })}
                />
                {errors.drive_end_date && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.drive_end_date.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="college_visit_date">College Visit Date</Label>
                <Input
                  id="college_visit_date"
                  type="date"
                  lang="en-GB"
                  {...register("college_visit_date")}
                />
              </div>
              <div>
                <Label htmlFor="joining_date">Joining Date</Label>
                <Input
                  id="joining_date"
                  type="date"
                  lang="en-GB"
                  {...register("joining_date")}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="start_time">Assessment Start Time (IST)</Label>
                <Input
                  id="start_time"
                  type="time"
                  max={isSameRegistrationDay ? watchEndTime || undefined : undefined}
                  {...register("start_time", {
                    validate: validateStartTime,
                  })}
                  placeholder="09:00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Indian Standard Time (UTC+5:30)
                </p>
                {errors.start_time && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.start_time.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="end_time">Assessment End Time (IST)</Label>
                <Input
                  id="end_time"
                  type="time"
                  min={isSameRegistrationDay ? watchStartTime || undefined : undefined}
                  {...register("end_time", {
                    validate: validateEndTime,
                  })}
                  placeholder="17:00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Indian Standard Time (UTC+5:30)
                </p>
                {errors.end_time && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.end_time.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="duration_minutes">
                  Assessment Duration (minutes)
                </Label>
                <Input
                  id="duration_minutes"
                  type="number"
                  min="1"
                  {...register("duration_minutes", {
                    valueAsNumber: true,
                    min: {
                      value: 1,
                      message: "Duration must be at least 1 minute",
                    },
                     validate: (value) => {
                       if (value && (!watchStartTime || !watchEndTime)) {
                         return "Start and end times are required";
                       }
                       if (value && watchStartTime && watchEndTime) {
                         const scheduleWindowMinutes = getScheduleWindowMinutes();
                         if (scheduleWindowMinutes != null && scheduleWindowMinutes < 0) {
                           return "Registration end date and time must be after the start date and time";
                         }
                         const availableMinutes = scheduleWindowMinutes ??
                           (parseTimeToMinutes(watchEndTime) - parseTimeToMinutes(watchStartTime));

                         if (value > availableMinutes) {
                           return "Duration cannot exceed available time";
                         }
                       }
                       return true;
                    },
                  })}
                  placeholder="120"
                />
                {errors.duration_minutes && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.duration_minutes.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="ip_range">IP Range</Label>
              <Textarea
                id="ip_range"
                {...register("ip_range")}
                placeholder="192.168.1.0/24&#10;10.0.0.1&#10;172.16.0.0/16"
                rows={3}
                className="text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter IP addresses or ranges, one per line. Supports CIDR
                notation (e.g., 192.168.1.0/24) and individual IPs.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Contact Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="placement_coordinator_name">
                  Placement Coordinator Name
                </Label>
                <Input
                  id="placement_coordinator_name"
                  {...register("placement_coordinator_name")}
                  placeholder="Enter coordinator name"
                />
              </div>

              <div>
                <Label htmlFor="placement_coordinator_phone">
                  Placement Coordinator Phone
                </Label>
                <Input
                  id="placement_coordinator_phone"
                  {...register("placement_coordinator_phone", {
                    pattern: {
                      value: phonePattern,
                      message: "Invalid phone number format",
                    },
                  })}
                  placeholder="+1234567890"
                />
                {errors.placement_coordinator_phone && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.placement_coordinator_phone.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="placement_officer_name">
                  Placement Officer Name
                </Label>
                <Input
                  id="placement_officer_name"
                  {...register("placement_officer_name")}
                  placeholder="Enter officer name"
                />
              </div>

              <div>
                <Label htmlFor="placement_officer_phone">
                  Placement Officer Phone
                </Label>
                <Input
                  id="placement_officer_phone"
                  {...register("placement_officer_phone", {
                    pattern: {
                      value: phonePattern,
                      message: "Invalid phone number format",
                    },
                  })}
                  placeholder="+1234567890"
                />
                {errors.placement_officer_phone && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.placement_officer_phone.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Academic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Courses Offered</Label>
                <Controller
                  name="courses"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={courseOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select courses..."
                      allowCustom={true}
                    />
                  )}
                />
              </div>

              <div>
                <Label>Branches Available</Label>
                <Controller
                  name="branches"
                  control={control}
                  render={({ field }) => (
                    <MultiSelect
                      options={branchOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder="Select branches..."
                      allowCustom={true}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              rows={4}
              placeholder="Additional notes about the college, special requirements, etc."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : college
                ? "Update College"
                : "Create College"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
